import {
  DashboardWidgetError,
  DashboardWidgetLoading,
} from '@/components/custom/DashboardWidgetState';
import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAgentsApi } from '@/hooks/useAgentsApi';
import { useApi } from '@/hooks/useApi';
import { toApiError } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import { Activity, ArrowRight, Building2, DollarSign, TrendingUp, Users } from 'lucide-react';
import { Link, Route, Routes } from 'react-router-dom';
import PayoutsPage from '../shared/PayoutsPage';
import AgentAffiliateDetail from './AgentAffiliateDetail';
import AgentAffiliates from './AgentAffiliates';
import AgentCommission from './AgentCommission';
import AgentProfile from './AgentProfile';

function AgentDashboardHome() {
  const agentsApi = useAgentsApi();
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['agent-dashboard'],
    queryFn: async () => {
      const response = await agentsApi.getDashboard();
      if (!response.success) throw toApiError(response, 'Failed to load dashboard data');
      return response.data;
    },
    refetchInterval: 60000, // Refresh every 60 seconds
  });

  const metrics = data?.metrics;
  const isNewAgent =
    data &&
    !metrics?.total_affiliates &&
    !metrics?.total_commission &&
    !metrics?.pending_commission;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Agent Dashboard</h1>
        <p className="mt-1 text-muted-foreground">
          Welcome back, {data?.agent?.name || 'Agent'}. Here's your territory overview.
        </p>
      </div>

      {/* Stats Grid */}
      {isLoading ? (
        <DashboardWidgetLoading label="Loading territory overview…" />
      ) : error ? (
        <DashboardWidgetError
          title="Territory overview unavailable"
          description="We couldn't load your latest territory metrics. Your agent tools remain available below."
          error={error}
          context={{ portal: 'agent', widget: 'overview', endpoint: '/agents/dashboard' }}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Affiliates"
            value={metrics?.total_affiliates || 0}
            subtitle={`${metrics?.active_affiliates || 0} active`}
            icon={Building2}
            href="/agent/affiliates"
            delay={0}
          />
          <StatCard
            title="Pending Approvals"
            value={metrics?.pending_approvals || 0}
            subtitle="Awaiting review"
            icon={Users}
            href="/agent/affiliates?status=pending"
            delay={50}
          />
          <StatCard
            title="Total Commission"
            value={formatCurrency(metrics?.total_commission || 0)}
            subtitle="All time earnings"
            icon={DollarSign}
            href="/agent/commission"
            delay={100}
          />
          <StatCard
            title="Pending Commission"
            value={formatCurrency(metrics?.pending_commission || 0)}
            subtitle="Awaiting payout"
            icon={TrendingUp}
            href="/agent/commission"
            delay={150}
          />
        </div>
      )}

      {/* New Agent CTA */}
      {isNewAgent && (
        <Card
          className="mt-8 border-primary/20 bg-primary/5 animate-fade-in"
          style={{ animationDelay: '200ms' }}
        >
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-primary/20 p-4 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              Get started by signing your first affiliate
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Share your unique referral link with gym owners to start building your network and
              earning commissions.
            </p>
            <Link to="/agent/profile">
              <Button>
                Get Your Referral Link
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Recent Affiliates</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              View and manage all affiliates in your territory.
            </p>
            <Link to="/agent/affiliates">
              <Button variant="outline" className="w-full">
                View All Affiliates
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Commission Ledger</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Track your earnings from subscriptions and equipment sales.
            </p>
            <Link to="/agent/commission">
              <Button variant="outline" className="w-full">
                View Commission
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '350ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Your Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              View your Shopify affiliate link and commission rates.
            </p>
            <Link to="/agent/profile">
              <Button variant="outline" className="w-full">
                View Profile
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <AgentActivityCard />
    </DashboardLayout>
  );
}

function AgentActivityCard() {
  const { api } = useApi();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['agent-activity'],
    queryFn: async () => {
      const response = await api.get<{
        activity: Array<{
          type: string;
          user_name: string;
          detail: string;
          affiliate_name: string;
          occurred_at: string;
        }>;
      }>('/agents/activity');
      if (!response.success) throw toApiError(response, 'Failed to load recent activity');
      return response.data;
    },
    refetchInterval: 120000,
  });

  const activity = data?.activity || [];

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Card className="mt-6 animate-fade-in" style={{ animationDelay: '400ms' }}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <DashboardWidgetLoading embedded label="Loading recent activity…" />
        ) : error ? (
          <DashboardWidgetError
            embedded
            title="Recent activity unavailable"
            description="We couldn't load recent territory activity."
            error={error}
            context={{ portal: 'agent', widget: 'recent-activity', endpoint: '/agents/activity' }}
            onRetry={() => void refetch()}
            isRetrying={isFetching}
          />
        ) : activity.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No recent activity from your affiliates yet. Activity will appear here as members log
            scores and join gyms.
          </p>
        ) : (
          <div className="space-y-3">
            {activity.map((item, i) => (
              <div
                key={`${item.occurred_at}-${i}`}
                className="flex items-start gap-3 text-sm border-b border-border pb-3 last:border-0 last:pb-0"
              >
                <div className="flex-1 min-w-0">
                  <p>
                    <span className="font-medium">{item.user_name}</span>{' '}
                    {item.type === 'score'
                      ? `logged a score on ${item.detail}`
                      : 'joined as a new member'}
                  </p>
                  <p className="text-xs text-muted-foreground">{item.affiliate_name}</p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatTimeAgo(item.occurred_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AgentDashboard() {
  return (
    <Routes>
      <Route path="/" element={<AgentDashboardHome />} />
      <Route path="affiliates" element={<AgentAffiliates />} />
      <Route path="affiliates/:affiliateId" element={<AgentAffiliateDetail />} />
      <Route path="commission" element={<AgentCommission />} />
      <Route path="payouts" element={<PayoutsPage />} />
      <Route path="profile" element={<AgentProfile />} />
    </Routes>
  );
}
